import { useEffect, useRef } from 'react';

// Point 1 — app-wide scroll preservation for list screens.
// Attach the returned ref to the scrollable container (the overflow-y-auto
// element). Scroll position is saved continuously (so it is always fresh when
// the user navigates away) under a unique sessionStorage `key`, and restored
// once after the list has painted. Pass `deps` (e.g. the item count) so restore
// fires after async data arrives.
//
// Usage:
//   const scrollRef = useScrollRestore('client_list_scroll', [items.length]);
//   <div ref={scrollRef} className="overflow-y-auto"> ... </div>
export function useScrollRestore(key, deps = []) {
  const ref = useRef(null);
  const restored = useRef(false);

  // Save on every scroll so the value is up-to-date the instant we unmount.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onScroll = () => {
      try { sessionStorage.setItem(key, String(el.scrollTop)); } catch (_) {}
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [key]);

  // Restore once, after the list content has rendered (deps = data length).
  useEffect(() => {
    const el = ref.current;
    if (!el || restored.current) return;
    try {
      const saved = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (saved > 0) {
        requestAnimationFrame(() => { if (ref.current) ref.current.scrollTop = saved; });
      }
      restored.current = true;
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

export default useScrollRestore;
